import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHead from "@/components/base/SEOHead";
import { categories, getCategoryInfo, getCategoryProducts } from "@/mocks/categoryData";

export default function CategoryPage() {
  const { slug = "all" } = useParams();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug);

  const categoryInfo = getCategoryInfo(activeCategory);
  const products = getCategoryProducts(activeCategory);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://luojunji1.cn/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "产品分类",
        "item": "https://luojunji1.cn/category/all",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": categoryInfo.name,
        "item": `https://luojunji1.cn/category/${activeCategory}`,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryInfo.name} - 爱威奶产品分类`}
        description={`浏览爱威奶${categoryInfo.name}的所有产品与文章，了解详细的产品信息、使用指南和真实用户评测。`}
        keywords={`爱威奶,${categoryInfo.name},爱威奶产品,爱威奶分类`}
        canonicalUrl={`https://luojunji1.cn/category/${activeCategory}`}
        ogTitle={`${categoryInfo.name} - 爱威奶产品分类`}
        ogDescription={`浏览爱威奶${categoryInfo.name}的所有产品与文章，了解详细的产品信息、使用指南和真实用户评测。`}
        ogUrl={`https://luojunji1.cn/category/${activeCategory}`}
        schemaJson={breadcrumbSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 py-10 md:py-14">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">产品分类</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">{categoryInfo.name}</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900">
              {categoryInfo.name}
            </h1>
            <p className="mt-2 text-sm text-foreground-600">
              探索爱威奶{categoryInfo.name}，发现适合您的优质产品与实用信息
            </p>
          </div>
        </div>

        <div className="w-full max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                <span>{cat.name}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/article/${product.id}`}
                className="group bg-background-50 border border-background-200/60 rounded-lg overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    title={product.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                    <span className="bg-accent-50 text-accent-700 px-2 py-0.5 rounded-full">{product.category}</span>
                    <span>{product.date}</span>
                    <span>{product.readTime}</span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {product.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {product.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {products.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无内容</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}